import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, Package, Download, Clock, CheckCircle, 
  FileText, Image, Video, Music, Archive, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { handleImageError } from '@/utils/imageUtils';
import ResilientImg from '@/components/ResilientImg';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// File type icon mapping
const getFileIcon = (filename) => {
  const ext = filename?.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'pdf':
    case 'doc':
    case 'docx':
    case 'txt':
    case 'md':
      return <FileText className="w-5 h-5" />;
    case 'png':
    case 'jpg':
    case 'jpeg':
    case 'gif':
    case 'webp':
      return <Image className="w-5 h-5" />;
    case 'mp4':
    case 'mov':
    case 'avi':
      return <Video className="w-5 h-5" />;
    case 'mp3':
    case 'wav':
    case 'aac':
      return <Music className="w-5 h-5" />;
    case 'zip':
    case 'rar':
    case '7z':
      return <Archive className="w-5 h-5" />;
    default:
      return <FileText className="w-5 h-5" />;
  }
};

// Format file size
const formatFileSize = (bytes) => {
  if (!bytes) return 'Unknown size';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

function MyPurchases() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingFile, setDownloadingFile] = useState(null);

  useEffect(() => {
    loadPurchases();
  }, []);

  const loadPurchases = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Please sign in to view your purchases');
        navigate('/');
        return;
      }

      const response = await axios.get(`${API_URL}/api/my-purchases`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPurchases(response.data);
    } catch (error) {
      console.error('Error loading purchases:', error);
      if (error.response?.status === 401) {
        toast.error('Please sign in to view your purchases');
        navigate('/');
      } else {
        toast.error('Failed to load purchases');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (orderId, productId, file) => {
    try {
      setDownloadingFile(file.file_id);
      const token = localStorage.getItem('token');
      
      // Generate download token
      const tokenRes = await axios.post(
        `${API_URL}/api/downloads/generate-token`,
        null,
        {
          params: { order_id: orderId, product_id: productId },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      // Open download URL
      const downloadUrl = `${API_URL}/api/downloads/${tokenRes.data.token}?file_id=${file.file_id}`;
      window.open(downloadUrl, '_blank');
      toast.success('Download started!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error(error.response?.data?.detail || 'Failed to download file');
    } finally {
      setDownloadingFile(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#00F0FF]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <header className="border-b border-white/10 backdrop-blur-xl bg-black/40 sticky top-0 z-50">
        <div className="container mx-auto px-6 md:px-12 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-[#00F0FF] hover:text-[#00C2CC]"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Marketplace
          </Button>
        </div>
      </header>

      <div className="container mx-auto px-6 md:px-12 py-12">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
            <Package className="w-8 h-8 text-[#00F0FF]" />
            My Purchases
          </h1>
          <p className="text-neutral-400 mb-8">Access and download your purchased products</p>

          {purchases.length === 0 ? (
            <div className="text-center py-20">
              <Package className="w-16 h-16 text-neutral-600 mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">No purchases yet</h2>
              <p className="text-neutral-400 mb-6">Your purchased products will appear here</p>
              <Button
                onClick={() => navigate('/')}
                className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black rounded-full px-6"
              >
                Browse Products
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {purchases.map((order) => (
                <div
                  key={order.order_id}
                  className="bg-neutral-900/50 border border-white/10 rounded-2xl overflow-hidden"
                >
                  {/* Order Header */}
                  <div className="bg-neutral-800/50 px-6 py-4 flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span className="text-sm text-green-500 font-medium">Completed</span>
                      </div>
                      <p className="text-xs text-neutral-400">
                        Order #{order.order_id?.slice(-8)} • {new Date(order.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-neutral-400">Total Paid</p>
                      <p className="text-lg font-bold text-[#00F0FF]">${order.total?.toFixed(2)}</p>
                    </div>
                  </div>

                  {/* Order Items */}
                  <div className="p-6 space-y-4">
                    {order.items?.map((item, idx) => (
                      <div key={idx} className="flex flex-col md:flex-row gap-4">
                        {/* Product Image */}
                        <div className="w-full md:w-24 h-24 rounded-xl overflow-hidden bg-neutral-800 flex-shrink-0">
                          {item.product_details?.images?.[0] ? (
                            <ResilientImg
                              src={item.product_details.images[0].startsWith('/api')
                                ? `${API_URL}${item.product_details.images[0]}`
                                : item.product_details.images[0]
                              }
                              alt={item.product_name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Package className="w-8 h-8 text-neutral-600" />
                            </div>
                          )}
                        </div>

                        {/* Product Info */}
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg mb-1">{item.product_name}</h3>
                          <p className="text-neutral-400 text-sm mb-3">
                            Qty: {item.quantity} • ${item.item_total?.toFixed(2)}
                          </p>

                          {/* Download Files */}
                          {item.files && item.files.length > 0 ? (
                            <div className="space-y-2">
                              <p className="text-xs text-neutral-500 uppercase tracking-wider">Downloads</p>
                              {item.files.map((file) => (
                                <div
                                  key={file.file_id}
                                  className="flex items-center justify-between bg-neutral-800/50 rounded-lg p-3"
                                >
                                  <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-[#00F0FF]/10 flex items-center justify-center text-[#00F0FF]">
                                      {getFileIcon(file.original_filename)}
                                    </div>
                                    <div>
                                      <p className="text-sm font-medium truncate max-w-[200px]">
                                        {file.original_filename}
                                      </p>
                                      <p className="text-xs text-neutral-500">
                                        {formatFileSize(file.file_size)}
                                      </p>
                                    </div>
                                  </div>
                                  <Button
                                    size="sm"
                                    onClick={() => handleDownload(order.order_id, item.product_id, file)}
                                    disabled={downloadingFile === file.file_id}
                                    className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black"
                                  >
                                    {downloadingFile === file.file_id ? (
                                      <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                      <>
                                        <Download className="w-4 h-4 mr-1" />
                                        Download
                                      </>
                                    )}
                                  </Button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 text-neutral-500 text-sm">
                              <Clock className="w-4 h-4" />
                              <span>No files available yet - seller will upload soon</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MyPurchases;
